<template>
  <div>
    <div class="rehab-title">动优康复课程中心</div>
    <div class="rehab-home__search">
        <van-search placeholder="搜索课程/分类" shape="round" v-model="keyword" @search="onSearch" />
      </div>
    
    <!-- 下拉刷新容器 -->
    <van-pull-refresh v-model="refreshing" @refresh="onRefresh">
      <!-- 加载错误状态 -->
      <div v-if="loadError && !loading" class="error-state" @click="retryLoad">
        <div class="error-state__icon">
          <van-icon name="warning-o" size="48" color="#ff6b6b" />
        </div>
        <div class="error-state__title">网络加载失败</div>
        <div class="error-state__desc">请检查网络连接，点击重新加载</div>
        <div class="error-state__retry">
          <van-button type="primary" size="small" @click.stop="retryLoad">重新加载</van-button>
        </div>
      </div>
      
      <!-- 正常内容 -->
      <div v-else class="rehab-home">
        <div class="rehab-tags" v-if="tagList && tagList.length">
          <div 
            class="rehab-tag"
            :class="{ 'rehab-tag--active': activeTag === tag }"
            v-for="tag in tagList"
            :key="tag"
            @click="selectTag(tag)"
          >{{ tag }}</div>
        </div>
        
        <div class="rehab-home__sections">
          <div class="rehab-home__section-title">精选课程</div>
          <div class="rehab-card-list">
            <div class="rehab-card" v-for="item in filteredCourses" :key="item.id" @click="goDetail(item)">
              <div class="rehab-card__banner" :style="{ backgroundImage: 'url(' + item.coverUrl + ')' }">
                <div class="rehab-card__badge" :class="item.vip ? 'rehab-card__badge--vip' : 'rehab-card__badge--free'">{{ item.vip ? 'vip会员课程' : '免费' }}</div>
                <div class="rehab-card__category">{{ item.type }}</div>
                <div class="rehab-card__banner-title">{{ item.title }}</div>
              </div>
              <div class="rehab-card__body">
                <div class="rehab-card__title">{{ item.title }}</div>
                <div class="rehab-card__tags">
                  <span class="tag">{{ item.type }}</span>
                </div>
                <div class="rehab-card__desc">{{ item.briefText || '暂无简介' }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </van-pull-refresh>

    <!-- VIP购买弹窗 -->
    <vip-purchase-popup 
      :show="showVipPopup" 
      @close="showVipPopup = false"
      @purchase="onVipPurchase"
    />

    <!-- 康复课程欢迎弹窗 -->
    <van-popup 
      v-model="showWelcomePopup" 
      closeable 
      close-icon-position="top-right"
      :style="{ borderRadius: '16px', maxWidth: '90vw', width: '340px' }"
      position="center"
      overlay
    >
      <div class="welcome-popup">
        <div class="welcome-popup__content">
          <!-- 头部图标 -->
          <div class="welcome-popup__header">
            <div class="welcome-popup__icon">
              <van-icon name="like-o" size="32" color="#07c160" />
            </div>
            <div class="welcome-popup__title">欢迎来到康复课程中心</div>
            <div class="welcome-popup__subtitle">专业康复指导，重塑健康生活</div>
          </div>

          <!-- 特色介绍 -->
          <div class="welcome-popup__features">
            <div class="welcome-popup__feature">
              <div class="welcome-popup__feature-icon">
                <van-icon name="star-o" size="20" color="#07c160" />
              </div>
              <div class="welcome-popup__feature-content">
                <div class="welcome-popup__feature-title">专业课程体系</div>
                <div class="welcome-popup__feature-desc">医学专家团队精心设计，科学有效</div>
              </div>
            </div>
            
            <div class="welcome-popup__feature">
              <div class="welcome-popup__feature-icon">
                <van-icon name="play-circle-o" size="20" color="#07c160" />
              </div>
              <div class="welcome-popup__feature-content">
                <div class="welcome-popup__feature-title">随时随地学习</div>
                <div class="welcome-popup__feature-desc">支持离线观看，灵活安排学习时间</div>
              </div>
            </div>
            
            <div class="welcome-popup__feature">
              <div class="welcome-popup__feature-icon">
                <van-icon name="friends-o" size="20" color="#07c160" />
              </div>
              <div class="welcome-popup__feature-content">
                <div class="welcome-popup__feature-title">个性化指导</div>
                <div class="welcome-popup__feature-desc">根据您的康复进度定制学习计划</div>
              </div>
            </div>
          </div>

          <!-- 激励文案 -->
          <div class="welcome-popup__motivation">
            <p>每一次学习都是向健康迈进的一步</p>
            <p>让我们一起开启您的康复之旅吧！</p>
          </div>

          <!-- 操作按钮 -->
          <div class="welcome-popup__actions">
            <van-button 
              type="primary" 
              color="linear-gradient(135deg, #07c160 0%, #06a84e 100%)"
              round 
              block 
              size="large"
              @click="startLearning"
            >
              开始探索课程
            </van-button>
          </div>
        </div>
      </div>
    </van-popup>
  </div>
</template>
<script>
import BaseTitleBarScrollPage from '@/h5/doucan/components/BaseTitleBarScrollPage.vue'
import VipPurchasePopup from '@/h5/doucan/components/VipPurchasePopup.vue'
import TicaoDao from '@/h5/doucan/network/Ticao.js'
import VipDao from '@/h5/doucan/network/VipDao.js'
import { log } from '@tensorflow/tfjs-core/dist/log'

export default {
  name: 'RehabCoursesHome',
  components: { 
    BaseTitleBarScrollPage,
    VipPurchasePopup
  },
  data() {
    return {
      isApp: false,
      keyword: '',
      recommendList: [],
      allCourses: [], // 保存原始数据用于搜索
      tagList: [],
      activeTag: '全部',
      refreshing: false, // 下拉刷新状态
      showVipPopup: false, // VIP购买弹窗显示状态
      showWelcomePopup: false, // 欢迎弹窗显示状态
      loadError: false, // 加载失败状态
      loading: true // 初始加载状态
    }
  },
  computed: {
    filteredCourses() {
      const key = (this.keyword || '').trim().toLowerCase()
      const matchesKeyword = (c) => {
        if (!key) return true
        return c.title.toLowerCase().includes(key) || (c.type || '').toLowerCase().includes(key)
      }
      const matchesTag = (c) => {
        if (this.activeTag === '全部') return true
        if (this.activeTag === '免费') return !c.vip
        if (this.activeTag === 'vip会员课程') return !!c.vip
        return c.type === this.activeTag
      }
      const base = this.recommendList
      return base.filter(c => matchesKeyword(c) && matchesTag(c))
    }
  },
  mounted() {
    const params = this.$route.query
    console.log('params',params);
    
    this.isApp = params.isApp=='true'
    
    // 获取小程序环境参数
    this.initMiniProgramParams(params)
    
    this.loadCourses()
    this.checkAndShowWelcome()
  },
  methods: {
    // 初始化小程序环境参数
    initMiniProgramParams(urlParams) {
      const miniProgramParams = {
        from: urlParams.from || '',
        hospitalId: urlParams.hospitalId || '',
        openid: urlParams.openid || ''
      }
      
      console.log('小程序环境参数:', miniProgramParams)
      
      // 如果是小程序环境，存储到store（会自动持久化）
      if (miniProgramParams.from === 'wxapp') {
        this.$store.commit('user/setMiniProgramParams', miniProgramParams)
        console.log('已将小程序参数存储到store')
      }
    },
    
    async loadCourses(showLoading = true) {
      try {
        // 重置错误状态
        this.loadError = false
        if (showLoading) {
          this.loading = true
        }
        
        const data = await TicaoDao.listApplet()
        console.log('课程数据结构:', data)
        if (data && Array.isArray(data)) {
          // 数据映射，添加默认字段
          this.allCourses = data.map(item => ({
            ...item,
            vipOnly: false, // 默认免费
            isFree: true,   // 默认免费
            duration: '待定', // 默认时长
            desc: item.type, // 描述使用类型
            intro: item.intro, // 详情
            briefText: item.briefText, // 简介
            vip: item.vip // 是否会员
          }))
          this.recommendList = this.allCourses
          // 生成标签（分类）列表
          const types = Array.from(new Set(this.allCourses.map(i => i.type).filter(Boolean)))
          this.tagList = ['全部', '免费', 'vip会员课程', ...types]
        } else {
          console.warn('课程数据格式异常:', data)
          this.recommendList = []
          this.tagList = []
        }
      } catch (error) {
        console.error('加载课程数据失败:', error)
        // 设置加载错误状态
        this.loadError = true
        // 如果是下拉刷新，抛出错误让调用方处理
        if (!showLoading) {
          throw error
        }
      } finally {
        // 设置加载完成状态
        this.loading = false
        // 只有在非下拉刷新时才隐藏loading
        if (showLoading) {
          this.hideLoading()
        }
      }
    },
    hideLoading() {
      this.$refs.container && this.$refs.container.stopLoading && this.$refs.container.stopLoading()
    },
    selectTag(tag) {
      this.activeTag = tag
    },
    onSearch() {
      // 搜索功能已通过 computed 属性 filteredCourses 实现
      console.log('搜索关键词:', this.keyword)
    },
    goDetail(item) {
      // 直接跳转到详情页，VIP检查逻辑移到详情页处理
      this.$router.push({
        name: 'RehabCourseDetail',
        query: {
          id: item.id,
          isApp: this.isApp ? '1' : '0'
        }
      })
    },
    checkLogin() {
      return this.$store.state.user.profile.isLogin
    },
    // 下拉刷新处理
    async onRefresh() {
      try {
        // 重新加载课程数据，不显示loading
        await this.loadCourses(false)
        this.$toast('刷新成功')
      } catch (error) {
        console.error('刷新失败:', error)
        this.$toast('刷新失败，请重试')
      } finally {
        // 结束刷新状态
        this.refreshing = false
      }
    },
    // VIP购买处理
    onVipPurchase(plan) {
      console.log('用户选择购买VIP套餐:', plan)
      // 这里可以跳转到支付页面或调用支付接口
      this.$toast(`对接中....正在跳转到${plan.name}购买页面...`)
      // TODO: 实现具体的购买逻辑
      // this.$router.push(`/vip/purchase?planId=${plan.id}`)
    },
    // 重试加载
    retryLoad() {
      this.loadCourses()
    },
    // 检查并显示欢迎弹窗
    checkAndShowWelcome() {
      // 使用sessionStorage标记，同一次会话中只显示一次
      const sessionKey = 'rehab_welcome_shown'
      const hasShown = sessionStorage.getItem(sessionKey)
      
      if (!hasShown) {
        // 延迟显示，等待页面加载完成
        setTimeout(() => {
          this.showWelcomePopup = true
          sessionStorage.setItem(sessionKey, 'true')
        }, 800)
      }
    },
    // 开始学习按钮点击
    startLearning() {
      this.showWelcomePopup = false
      // 可以添加一些引导逻辑，比如滚动到课程列表
      this.$nextTick(() => {
        // 如果有课程，滚动到第一个课程
        if (this.filteredCourses.length > 0) {
          const firstCard = document.querySelector('.rehab-card')
          if (firstCard) {
            firstCard.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }
        }
      })
    }
  }
}
</script>
<style scoped>
.rehab-title { text-align: center; font-size: 18px; font-weight: 600; padding: 10px 0; background: #07c160; color: #fff; }
.rehab-home {
  padding: 12px;
}

.rehab-home__search {
  margin-bottom: 8px;
  position: sticky;
  top: 0;
  z-index: 20;
  background: #07c160;
  padding: 8px 12px;
}
/* 覆盖 vant 默认白底，让外层绿色可见 */
.rehab-home__search ::v-deep .van-search { background: transparent !important; padding: 0 !important; }
.rehab-home__search ::v-deep .van-search__content { background: #fff; }

.rehab-home__sections { margin-top: 8px; }

.rehab-home__section-title { font-size: 16px; font-weight: bold; margin-bottom: 8px; color: #07c160; }

.rehab-tags { display: flex; gap: 8px; overflow-x: auto; padding: 8px 0 4px; scrollbar-width: none; -ms-overflow-style: none; }
.rehab-tags::-webkit-scrollbar { display: none; }
.rehab-tag { flex: 0 0 auto; padding: 6px 12px; border-radius: 999px; background: #f6fdf9; color: #059669; border: 1px solid #a7f3d0; font-size: 13px; font-weight: 600; }
.rehab-tag--active { background: #10b981; color: #fff; border-color: #10b981; box-shadow: 0 4px 10px rgba(16,185,129,0.2); }

.rehab-card-list { display: grid; grid-template-columns: 1fr; gap: 12px; }
.rehab-card { background: #fff; border-radius: 12px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.06); }
.rehab-card__banner { position: relative; height: 140px; background-size: cover; background-position: center; display: flex; flex-direction: column; justify-content: flex-end; padding: 12px; color: #fff; }
.rehab-card__banner::after { content: ''; position: absolute; left: 0; right: 0; bottom: 0; height: 60%; background: linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.45) 60%, rgba(0,0,0,0.65) 100%); pointer-events: none;  }
.rehab-card__badge { position: absolute; right: 12px; top: 12px; color: #fff; font-size: 12px; padding: 4px 10px; border-radius: 999px; font-weight: 700; letter-spacing: 0.5px; box-shadow: 0 4px 10px rgba(0,0,0,0.15); backdrop-filter: saturate(140%) blur(2px); }
.rehab-card__badge--vip { background: linear-gradient(135deg, #ff8f3f 0%, #ff5b2e 100%); }
.rehab-card__badge--free { background: linear-gradient(135deg, #34d399 0%, #10b981 100%); }
.rehab-card__category { font-size: 12px; opacity: 0.9; }
.rehab-card__banner-title { font-size: 16px; font-weight: 700; }
.rehab-card__body { padding: 12px; }
.rehab-card__title { font-size: 16px; font-weight: 600; margin-bottom: 6px; }
.rehab-card__tags { display: flex; align-items: center; flex-wrap: wrap; gap: 6px; margin-bottom: 8px; }
.tag { display: inline-flex; align-items: center; padding: 2px 8px; font-size: 12px; line-height: 18px; border-radius: 999px; background: #f0fdf4; color: #059669; border: 1px solid #a7f3d0; font-weight: 600; }
.tag--free { background: #ecfdf5; color: #10b981; border-color: #99f6e4; }
.tag--vip { background: #fff7ed; color: #f97316; border-color: #fed7aa; }
.rehab-card__desc { color: #666; font-size: 13px; margin-bottom: 8px; display: -webkit-box; -webkit-line-clamp: 2; line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; text-overflow: ellipsis; }
.rehab-card__meta { color: #999; font-size: 12px; }

/* 错误状态样式 */
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  min-height: 400px;
  text-align: center;
  cursor: pointer;
}

.error-state__icon {
  margin-bottom: 16px;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
}

.error-state__title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}

.error-state__desc {
  font-size: 14px;
  color: #666;
  margin-bottom: 20px;
  line-height: 1.4;
}

.error-state__retry {
  margin-top: 8px;
}

/* 欢迎弹窗样式 */
.welcome-popup {
  max-width: 100%;
  max-height: 85vh;
  overflow-y: auto;
  background: #fff;
  border-radius: 16px;
}

.welcome-popup__content {
  padding: 28px 24px 32px;
  background: #fff;
  border-radius: 16px;
}

.welcome-popup__header {
  text-align: center;
  margin-bottom: 20px;
}

.welcome-popup__icon {
  margin-bottom: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.welcome-popup__title {
  font-size: 20px;
  font-weight: 700;
  color: #323233;
  margin-bottom: 8px;
  line-height: 1.3;
}

.welcome-popup__subtitle {
  font-size: 14px;
  color: #646566;
  line-height: 1.4;
}

.welcome-popup__features {
  margin-bottom: 20px;
}

.welcome-popup__feature {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
  padding: 10px 12px;
  background: #f8fffe;
  border-radius: 8px;
  border-left: 3px solid #07c160;
  border-right: 3px solid #07c160;
}

.welcome-popup__feature:last-child {
  margin-bottom: 0;
}

.welcome-popup__feature-icon {
  flex-shrink: 0;
  margin-top: 2px;
}

.welcome-popup__feature-content {
  flex: 1;
}

.welcome-popup__feature-title {
  font-size: 15px;
  font-weight: 600;
  color: #323233;
  margin-bottom: 4px;
}

.welcome-popup__feature-desc {
  font-size: 13px;
  color: #646566;
  line-height: 1.4;
}

.welcome-popup__motivation {
  text-align: center;
  margin-bottom: 20px;
  padding: 14px;
  background: linear-gradient(135deg, #f0fdf8 0%, #ecfdf5 100%);
  border-radius: 12px;
  border: 1px solid #d1fae5;
}

.welcome-popup__motivation p {
  margin: 0 0 6px 0;
  font-size: 14px;
  color: #065f46;
  font-weight: 500;
  line-height: 1.5;
}

.welcome-popup__motivation p:last-child {
  margin-bottom: 0;
}

.welcome-popup__actions {
  text-align: center;
}

/* 弹窗动画优化 */
.welcome-popup ::v-deep .van-popup__close-icon {
  color: #969799;
  font-size: 20px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.welcome-popup ::v-deep .van-popup__close-icon:active {
  background-color: #f2f3f5;
  border-radius: 50%;
  transform: scale(0.9);
}

/* 中间弹出效果优化 */
::v-deep .van-popup--center.van-popup--round {
  border-radius: 16px;
}

::v-deep .van-overlay {
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(2px);
}
</style>
